/**
 * 游戏分享按钮组件
 * 为每个游戏提供分享功能，包括复制链接和显示推广URL
 */

import React, { useState } from 'react';
import { getGameLink, copyGameLink } from '../utils/gameLinks';

interface GameShareButtonProps {
  gameType: string;
  match3Mode?: 'classic' | 'stars' | 'happy' | 'fruit'; // 消消看游戏的模式
  className?: string;
}

const GameShareButton: React.FC<GameShareButtonProps> = ({
  gameType,
  match3Mode,
  className = ''
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // 处理消消看游戏的不同模式
  const getEffectiveGameType = () => {
    if (gameType === 'match3' && match3Mode) {
      return match3Mode;
    }
    return gameType;
  };

  const gameLink = getGameLink(getEffectiveGameType());
  const shareUrl = gameLink?.url || window.location.href;

  const handleCopyLink = async () => {
    const success = await copyGameLink(getEffectiveGameType());
    if (success) {
      setCopySuccess(true);
      setShowTooltip(true);
      setTimeout(() => {
        setShowTooltip(false);
        setCopySuccess(false);
      }, 2000);
    }
  };

  const handleShareClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // 使用Web Share API（如果可用）
    if (navigator.share && gameLink) {
      try {
        await navigator.share({
          title: gameLink.name,
          text: gameLink.shareText,
          url: shareUrl
        });
      } catch (err) {
        // 如果分享失败，回退到复制链接
        handleCopyLink();
      }
    } else {
      // 如果不支持分享API，直接复制链接
      handleCopyLink();
    }
  };

  if (!gameLink) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={handleShareClick}
        className={`
          flex items-center space-x-1 px-2 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs sm:text-sm font-medium ${className}
        `}
        title="分享游戏链接"
      >
        <span>🔗</span>
        <span className="hidden sm:inline">分享</span>
      </button>

      {/* 提示框 */}
      {showTooltip && (
        <div
          className={`
            absolute top-full mt-2 left-1/2 transform -translate-x-1/2
            px-3 py-2 rounded-lg text-white text-sm font-medium
            transition-all duration-200 z-50 pointer-events-none
            ${copySuccess ? 'bg-green-600' : 'bg-gray-700'}
          `}
          onClick={(e) => e.preventDefault()}
        >
          {copySuccess ? '✓ 链接已复制到剪贴板！' : '游戏链接'}

          {/* 显示URL - 只显示部分避免过长 */}
          <div className="text-xs mt-1 opacity-90 select-none">
            {shareUrl.length > 30 ? `${shareUrl.substring(0, 30)}...` : shareUrl}
          </div>
        </div>
      )}
    </div>
  );
};

export default GameShareButton;